import { useState, useEffect, useCallback } from 'react'
import { api } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtTime  = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'
const avatarBg = (str = '') => {
  const colors = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return colors[Math.abs(h) % colors.length]
}
const initials = (name = '') => (name.trim().charAt(0) || '?').toUpperCase()

const stars = (n, max = 5) =>
  Array.from({ length: max }, (_, i) => (
    <span key={i} style={{ color: i < n ? '#f59e0b' : '#e9e9e4', fontSize: 13 }}>★</span>
  ))

// ── Source type config ────────────────────────────────────────────────────────
const SOURCE = {
  reel:    { label: 'Clip Comment',    color: '#7c3aed', bg: '#f5f3ff', icon: '▶'  },
  product: { label: 'Product Rating',  color: '#1d4ed8', bg: '#eff6ff', icon: '🛍' },
  driver:  { label: 'Driver Rating',   color: '#15803d', bg: '#f0fdf4', icon: '🚚' },
  live:    { label: 'Live Comment',    color: '#b45309', bg: '#fffbeb', icon: '🔴' },
  video:   { label: 'Video Comment',   color: '#0d9488', bg: '#f0fdfa', icon: '🎬' },
}
const sourceStyle = (type) => SOURCE[type] ?? { label: type, color: '#6b7280', bg: '#f3f4f6', icon: '💬' }

// ── Shared ────────────────────────────────────────────────────────────────────
const SS = {
  select: {
    border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 12px',
    fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff',
    cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif",
  },
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, accent, sub }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '18px 20px', flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', color: accent || '#111317' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 4, fontFamily: "'JetBrains Mono',monospace" }}>{sub}</div>}
    </div>
  )
}

// ── Delete Confirm Modal ──────────────────────────────────────────────────────
function DeleteModal({ item, onClose, onConfirm, loading }) {
  if (!item) return null
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 300 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 400, background: '#fff', borderRadius: 14, zIndex: 301, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn{from{opacity:0;transform:translate(-50%,-48%) scale(0.97)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>
        <div style={{ padding: '28px 28px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🗑️</div>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Delete {['reel', 'live', 'video'].includes(item.type) ? 'Comment' : 'Rating'}?</div>
          <div style={{ fontSize: 13, color: '#9a9a94', lineHeight: 1.7 }}>
            This will permanently remove <b>{item.username || 'Anonymous'}</b>'s {['reel', 'live', 'video'].includes(item.type) ? 'comment' : 'review'}.
          </div>
        </div>
        <div style={{ padding: '0 28px 24px', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {loading ? 'Deleting…' : 'Yes, Delete'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Comment / Rating Card ─────────────────────────────────────────────────────
function ContentCard({ item, onDelete, onFlag }) {
  const [expanded, setExpanded] = useState(false)
  const src = sourceStyle(item.type)
  const bg  = avatarBg(item.username || '')
  const isLong = item.content?.length > 140

  return (
    <div style={{
      background: '#fff', border: `1px solid ${item.flagged ? 'rgba(239,68,68,0.4)' : '#e9e9e4'}`,
      borderLeft: `3px solid ${item.flagged ? '#ef4444' : src.color}`,
      borderRadius: 10, padding: '16px 18px',
      opacity: item.deleted ? 0.4 : 1,
    }}>
      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
        {/* Avatar */}
        <div style={{ width: 34, height: 34, borderRadius: 8, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#111317', flexShrink: 0 }}>
          {initials(item.username)}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{item.username || 'Anonymous'}</span>

            {/* Source badge */}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: src.bg, color: src.color, fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '2px 7px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {src.icon} {src.label}
            </span>

            {/* Flagged badge */}
            {item.flagged && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fef2f2', color: '#b91c1c', fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '2px 7px', borderRadius: 4, textTransform: 'uppercase' }}>
                ⚑ Flagged
              </span>
            )}
          </div>

          {/* Star rating (for product/driver) */}
          {item.rating != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
              <div style={{ display: 'flex' }}>{stars(item.rating)}</div>
              <span style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{item.rating}/5</span>
            </div>
          )}

          {/* Reference (reel title, product name, etc.) */}
          {item.reference && (
            <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              on: <span style={{ fontWeight: 600, color: '#374151' }}>{item.reference}</span>
            </div>
          )}
        </div>

        <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", flexShrink: 0, textAlign: 'right' }}>
          {fmtTime(item.created_at)}
        </div>
      </div>

      {/* Content */}
      <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, marginBottom: 12, paddingLeft: 44 }}>
        {isLong && !expanded
          ? <>{item.content.slice(0, 140)}<span style={{ color: '#9a9a94' }}>… </span><span onClick={() => setExpanded(true)} style={{ color: '#e8440a', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>more</span></>
          : <>{item.content}{isLong && <span onClick={() => setExpanded(false)} style={{ color: '#e8440a', cursor: 'pointer', fontWeight: 600, fontSize: 12, marginLeft: 6 }}>less</span>}</>
        }
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, paddingLeft: 44 }}>
        <button
          onClick={() => onFlag(item)}
          style={{ padding: '4px 12px', borderRadius: 6, border: `1px solid ${item.flagged ? '#fecaca' : '#e9e9e4'}`, background: item.flagged ? '#fef2f2' : '#fff', color: item.flagged ? '#b91c1c' : '#6b7280', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          {item.flagged ? '⚑ Unflag' : '⚑ Flag'}
        </button>
        <button
          onClick={() => onDelete(item)}
          style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          Delete
        </button>
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Comments() {
  const [items,       setItems]       = useState([])
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState('')
  const [tab,         setTab]         = useState('all')   // 'all' | 'flagged' | 'reel' | 'product' | 'driver'
  const [search,      setSearch]      = useState('')
  const [ratingFilter,setRatingFilter]= useState('')
  const [page,        setPage]        = useState(1)
  const [totalPages,  setTotalPages]  = useState(1)
  const [totalCount,  setTotalCount]  = useState(0)
  const [stats,       setStats]       = useState({ total: 0, flagged: 0, reel: 0, product: 0, driver: 0, video: 0 })
  const safeStats = (s) => ({ total: 0, flagged: 0, reel: 0, product: 0, driver: 0, live: 0, video: 0, ...s })
  const [deleting,    setDeleting]    = useState(null)
  const [deleteLoad,  setDeleteLoad]  = useState(false)

  const PER_PAGE = 20

  const fetchItems = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const params = new URLSearchParams({
        page, limit: PER_PAGE,
        ...(search        && { search }),
        ...(tab !== 'all' && tab !== 'flagged' && { type: tab }),
        ...(tab === 'flagged' && { flagged: '1' }),
        ...(ratingFilter  && { rating: ratingFilter }),
      })
      const res = await api.get(`/comments/admin/all?${params}`)
      setItems(res.data.items        || [])
      setTotalPages(res.data.totalPages || 1)
      setTotalCount(res.data.total      || 0)
      setStats(safeStats(res.data.stats || {}))
    } catch {
      setError('Failed to load — showing mock data')
      const types = ['reel', 'product', 'driver', 'live']
      const mock  = Array.from({ length: 16 }, (_, i) => ({
        id:         i + 1,
        type:       types[i % 4],
        username:   i % 5 === 0 ? null : `user_${i + 1}`,
        user_id:    i + 1,
        content:    i % 3 === 0
          ? 'This is absolutely terrible service!! I want my money back right now, this is unacceptable behavior from your shop.'
          : i % 7 === 0
            ? 'Great product! Loved it so much, will definitely buy again. Highly recommend to everyone!'
            : `Sample comment number ${i + 1} about this content item.`,
        rating:     ['product','driver'].includes(types[i % 4]) ? Math.ceil(Math.random() * 5) : null,
        reference:  types[i % 4] === 'reel' ? `Reel #${i + 1}` : types[i % 4] === 'product' ? `Product ${i + 1}` : types[i % 4] === 'driver' ? `Driver ${i + 1}` : `Stream ${i + 1}`,
        flagged:    i % 6 === 0,
        created_at: new Date(Date.now() - i * 3600000 * 6).toISOString(),
      }))
      setItems(mock)
      setTotalPages(3); setTotalCount(60)
      setStats(safeStats({ total: 60, flagged: 12, reel: 20, product: 18, driver: 14, live: 8 }))
    } finally { setLoading(false) }
  }, [page, tab, search, ratingFilter])

  useEffect(() => { fetchItems() }, [fetchItems])
  useEffect(() => { setPage(1)  }, [tab, search, ratingFilter])

  const handleDelete = async () => {
    if (!deleting) return
    setDeleteLoad(true)
    try {
      await api.delete(`/comments/admin/${deleting.type}/${deleting.id}`)
      setItems(prev => prev.filter(i => i.id !== deleting.id))
      setTotalCount(c => c - 1)
      setDeleting(null)
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete')
    } finally { setDeleteLoad(false) }
  }

  const handleFlag = async (item) => {
    try {
      await api.patch(`/comments/admin/${item.type}/${item.id}/flag`, { flagged: !item.flagged })
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, flagged: !i.flagged } : i))
    } catch { alert('Failed to update flag') }
  }

  const TABS = [
    { id: 'all',     label: 'All',          count: stats.total   },
    { id: 'flagged', label: '⚑ Flagged',    count: stats.flagged, accent: '#b91c1c' },
    { id: 'reel',    label: '▶ Clips',        count: stats.reel    },
    { id: 'video',   label: '🎬 Videos',      count: stats.video   },
    { id: 'product', label: '🛍 Products',   count: stats.product },
    { id: 'driver',  label: '🚚 Drivers',    count: stats.driver  },

  ]

  const hasRatingFilter = tab === 'product' || tab === 'driver'

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Comments & Ratings</div>
        <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Review all platform comments and ratings. Flag or remove policy-violating content.</div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
        <StatCard label="Total"     value={stats.total.toLocaleString()}                         />
        <StatCard label="Flagged"   value={stats.flagged.toLocaleString()}  accent="#b91c1c" sub="Needs review" />
        <StatCard label="Clip"      value={stats.reel.toLocaleString()}     accent="#7c3aed"     />
        <StatCard label="Video"     value={stats.video.toLocaleString()}    accent="#0d9488"     />
        <StatCard label="Product"   value={stats.product.toLocaleString()}  accent="#1d4ed8"     />
        <StatCard label="Driver"    value={stats.driver.toLocaleString()}   accent="#15803d"     />

      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: 5, width: 'fit-content', flexWrap: 'wrap' }}>
        {TABS.map(({ id, label, count, accent }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            style={{
              padding: '7px 14px', borderRadius: 7, border: 'none',
              background: tab === id ? '#111317' : 'transparent',
              color: tab === id ? '#fff' : accent || '#9a9a94',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'Plus Jakarta Sans',sans-serif",
              transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 6,
            }}>
            {label}
            <span style={{ background: tab === id ? 'rgba(255,255,255,0.2)' : '#f0f0ec', color: tab === id ? '#fff' : '#9a9a94', fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '1px 6px', borderRadius: 3, minWidth: 18, textAlign: 'center' }}>
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '11px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 12px' }}>
          <span style={{ fontSize: 14, color: '#9a9a94' }}>🔍</span>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by username or content…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111317', flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }}
          />
          {search && <span onClick={() => setSearch('')} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 16 }}>×</span>}
        </div>

        {hasRatingFilter && (
          <select value={ratingFilter} onChange={e => setRatingFilter(e.target.value)} style={SS.select}>
            <option value="">All Ratings</option>
            {[5,4,3,2,1].map(n => <option key={n} value={n}>{n} ★</option>)}
          </select>
        )}

        <button onClick={fetchItems} style={{ height: 36, width: 36, borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↻</button>
        <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>{totalCount.toLocaleString()} items</div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#b91c1c' }}>⚠ {error}</div>
      )}

      {/* Flagged alert */}
      {tab === 'all' && stats.flagged > 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderLeft: '3px solid #ef4444', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>⚑</span>
          <span><b>{stats.flagged} items flagged</b> for potential policy violations.</span>
          <span onClick={() => setTab('flagged')} style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#b91c1c', cursor: 'pointer', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>Review Flagged →</span>
        </div>
      )}

      {/* Content list */}
      {loading ? (
        <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '60px 0' }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '60px 32px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>💬</div>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>No {tab === 'flagged' ? 'flagged items' : 'items'} found</div>
          <div style={{ fontSize: 13, color: '#9a9a94' }}>{tab === 'flagged' ? 'All clear — no flagged content.' : 'Try a different filter.'}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {items.map(item => (
            <ContentCard
              key={`${item.type}-${item.id}`}
              item={item}
              onDelete={i => setDeleting(i)}
              onFlag={handleFlag}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>Page {page} of {totalPages}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
            })}
            <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}

      {/* Delete modal */}
      <DeleteModal item={deleting} onClose={() => setDeleting(null)} onConfirm={handleDelete} loading={deleteLoad} />
    </div>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ minWidth: 34, height: 34, borderRadius: 7, border: '1px solid #e9e9e4', background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#9a9a94' : '#111317', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', padding: '0 10px', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: disabled ? 0.4 : 1 }}>
      {label}
    </button>
  )
}